import { useCallback, useState } from "react";
import { saveChatVideos } from "../../../../services/contentService";
import {
  UGC_AI_SCRIPT_WRITER_PREFIX,
  UGC_AI_CAPTION_WRITER_PREFIX,
} from "../../../../features/ugc-video/constants";
import { sendStreamingChatMessage, type ChatStreamError } from "../../../../services/chatService";
import { type VideoForPlatform } from "../../../../features/video-management";
import { Platform } from "../../../../types/Platforms";
import {
  GeneratedVideo,
  Message,
} from "../types";

interface UseUgcVideoPanelParams {
  agentId?: string;
  customerId?: string;
  idToken: string;
  model?: string;
  vectorStoreId?: string;
  threadId: string | null;
  conversationId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setError: (value: ChatStreamError | string | null) => void;
  setRightPanelMode: (mode: "video" | "social_posts" | null) => void;
  getLastToolPreference: () => string | null;
}

export const useUgcVideoPanel = ({
  agentId,
  customerId,
  idToken,
  model,
  vectorStoreId,
  threadId,
  conversationId,
  setMessages,
  setError,
  setRightPanelMode,
  getLastToolPreference,
}: UseUgcVideoPanelParams) => {
  const [scriptContent, setScriptContent] = useState("");
  const [streamingScriptContent, setStreamingScriptContent] = useState("");
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [generatedCaption, setGeneratedCaption] = useState("");
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [isSavingVideos, setIsSavingVideos] = useState(false);
  const [showSaveToPlatform, setShowSaveToPlatform] = useState(false);
  const [videoForPlatform, setVideoForPlatform] = useState<VideoForPlatform | null>(null);
  const [saveToPlatformInitialPlatforms, setSaveToPlatformInitialPlatforms] = useState<Platform[]>([]);

  const shouldAutoOpenVideoPanel = useCallback(() => {
    const tool = getLastToolPreference();
    return tool !== "perplexity_search" && tool !== "generate_image";
  }, [getLastToolPreference]);

  const onScript = useCallback(
    (delta?: string, fullText?: string) => {
      if (fullText !== undefined) {
        setScriptContent(fullText);
        setStreamingScriptContent(fullText);
      } else if (delta) {
        setStreamingScriptContent((prev) => prev + delta);
      }
      if (shouldAutoOpenVideoPanel()) {
        setRightPanelMode("video");
      }
    },
    [setRightPanelMode, shouldAutoOpenVideoPanel]
  );

  const onScriptEnd = useCallback((accumulated?: string) => {
    if (accumulated) setScriptContent(accumulated);
  }, []);

  const onSocialDescription = useCallback(
    (items: Array<{ caption: string; tags: string[] }>) => {
      if (items.length === 0) return;
      const first = items[0];
      const captionWithTags = [first.caption, first.tags.join(" ")]
        .filter(Boolean)
        .join("\n\n");
      setGeneratedCaption(captionWithTags);
      if (shouldAutoOpenVideoPanel()) {
        setRightPanelMode("video");
      }
    },
    [setRightPanelMode, shouldAutoOpenVideoPanel]
  );

  const buildRequestBody = useCallback(
    (prompt: string) => {
      const requestBody: any = {
        agent_id: agentId,
        provider: "openai",
        messages: [
          {
            role: "user" as const,
            content: [{ type: "input_text", text: prompt }],
          },
        ],
        customer_id: customerId,
        model: model || "gpt-4",
      };
      if (vectorStoreId) requestBody.vector_store_id = vectorStoreId;
      if (threadId) requestBody.thread_id = threadId;
      if (conversationId) requestBody.conversation_id = conversationId;
      return requestBody;
    },
    [agentId, customerId, conversationId, model, threadId, vectorStoreId]
  );

  const handleGenerateScript = useCallback(
    async (prompt: string) => {
      if (!agentId || !customerId || !idToken) {
        setError("Missing required information for script generation");
        return;
      }

      setIsGeneratingScript(true);
      setStreamingScriptContent("");
      setError(null);

      try {
        const requestBody = buildRequestBody(`${UGC_AI_SCRIPT_WRITER_PREFIX}${prompt}`);
        const response = await sendStreamingChatMessage(requestBody, idToken);
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error("No response body");

        let buffer = "";
        let accumulatedText = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim() || !line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.substring(6));
              if (data.delta) {
                accumulatedText += data.delta;
                setStreamingScriptContent(accumulatedText);
              } else if (data.text || data.content) {
                accumulatedText = data.text || data.content;
                setStreamingScriptContent(accumulatedText);
              }
            } catch {
              // Ignore malformed chunks.
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && (err as any).chatStreamError) {
          setError((err as any).chatStreamError as ChatStreamError);
        } else {
          setError("Something went wrong while generating your script. Please try again later.");
        }
      } finally {
        setIsGeneratingScript(false);
      }
    },
    [agentId, buildRequestBody, customerId, idToken, setError]
  );

  /**
   * Writes the social post caption for a finished video from its script, mirroring how the
   * Social Posts flow generates a caption (same chat stream + agent). Streams the result into
   * `generatedCaption` so the Post Details caption box fills itself after the final render.
   */
  const handleGenerateCaption = useCallback(
    async (script: string) => {
      const context = (script || "").trim();
      if (!agentId || !customerId || !idToken || !context) return;

      setIsGeneratingCaption(true);
      setError(null);

      try {
        const requestBody = buildRequestBody(`${UGC_AI_CAPTION_WRITER_PREFIX}${context}`);
        const response = await sendStreamingChatMessage(requestBody, idToken);
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) throw new Error("No response body");

        let buffer = "";
        let accumulatedText = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim() || !line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.substring(6));
              if (data.delta) {
                accumulatedText += data.delta;
                setGeneratedCaption(accumulatedText);
              } else if (data.text || data.content) {
                accumulatedText = data.text || data.content;
                setGeneratedCaption(accumulatedText);
              }
            } catch {
              // Ignore malformed chunks.
            }
          }
        }
      } catch (err) {
        // Non-blocking: a failed auto-caption shouldn't interrupt the save flow; the user
        // can still type or save manually.
        setError("Caption generation failed. You can write one manually.");
      } finally {
        setIsGeneratingCaption(false);
      }
    },
    [agentId, buildRequestBody, customerId, idToken, setError]
  );

  const handleSaveVideos = useCallback(
    async (selectedVideos: GeneratedVideo[]) => {
      if (!customerId || !idToken || selectedVideos.length === 0) {
        setError("Unable to save videos. Missing required data.");
        return;
      }
      setIsSavingVideos(true);
      setError(null);
      try {
        const videosToSave = selectedVideos.map((video) => ({
          platform: video.platform,
          title: video.title,
          content: video.content,
          video_url: video.video_url,
          hashtags: video.hashtags || [],
          call_to_action: video.call_to_action || "",
        }));
        const result = await saveChatVideos(customerId, videosToSave, idToken);
        setMessages((prev: Message[]) => [
          ...prev,
          {
            role: "assistant",
            content: `✅ ${result.post_ids.length} ${
              result.post_ids.length === 1 ? "video" : "videos"
            } saved successfully! You can review and publish them in the Social Posts page.`,
          },
        ]);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to save videos. Please try again."
        );
      } finally {
        setIsSavingVideos(false);
      }
    },
    [customerId, idToken, setError, setMessages]
  );

  const handleSaveVideoToPlatform = useCallback(
    async (
      videoUrl: string,
      selectedPlatforms: Platform[],
      script?: string,
      generatedCaptionText?: string,
      tags?: string[],
      linkedinTarget?: "personal" | "business"
    ) => {
      if (!videoUrl?.trim()) {
        setError("Video URL is missing. Please regenerate the video and try again.");
        return;
      }
      if (selectedPlatforms.length === 0) {
        setVideoForPlatform({
          videoUrl,
          sourceType: "ugc",
          script: script || undefined,
          generatedCaption: generatedCaptionText || undefined,
        });
        setSaveToPlatformInitialPlatforms([]);
        setShowSaveToPlatform(true);
        return;
      }
      const content = (generatedCaptionText || script || "").trim() || "Video";
      const videosToSave = selectedPlatforms.map((p) => ({
        platform: p,
        title: "Video",
        content,
        video_url: videoUrl,
        hashtags: Array.isArray(tags) ? tags : [],
        call_to_action: "",
        ...(linkedinTarget ? { linkedinTarget } : {}),
      }));
      setIsSavingVideos(true);
      setError(null);
      try {
        const result = await saveChatVideos(customerId || "", videosToSave, idToken);
        setMessages((prev: Message[]) => [
          ...prev,
          {
            role: "assistant",
            content: `✅ ${result.post_ids.length} ${
              result.post_ids.length === 1 ? "video" : "videos"
            } saved to Review Posts! You can review and publish them in the Social Posts page.`,
          },
        ]);
        setRightPanelMode(null);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to save videos. Please try again."
        );
      } finally {
        setIsSavingVideos(false);
      }
    },
    [customerId, idToken, setError, setMessages, setRightPanelMode]
  );

  const resetVideoPanelDraft = useCallback(() => {
    setScriptContent("");
    setStreamingScriptContent("");
    setGeneratedCaption("");
  }, []);

  const closeSaveToPlatform = useCallback(() => {
    setShowSaveToPlatform(false);
    setVideoForPlatform(null);
    setSaveToPlatformInitialPlatforms([]);
  }, []);

  return {
    scriptContent,
    setScriptContent,
    streamingScriptContent,
    isGeneratingScript,
    generatedCaption,
    setGeneratedCaption,
    isGeneratingCaption,
    isSavingVideos,
    showSaveToPlatform,
    videoForPlatform,
    saveToPlatformInitialPlatforms,
    onScript,
    onScriptEnd,
    onSocialDescription,
    handleGenerateScript,
    handleGenerateCaption,
    handleSaveVideos,
    handleSaveVideoToPlatform,
    resetVideoPanelDraft,
    closeSaveToPlatform,
  };
};
